import React, { useEffect, useMemo, useState } from 'react';
import { GeoJSON } from 'react-leaflet';
import L from 'leaflet';
import pointOnFeature from '@turf/point-on-feature';
import axios from 'util/Api';
import { hasSession } from 'util/session';

const DEFAULT_STYLE = {
  color: '#6f6f6f',
  fillColor: '#6f6f6f',
  fillOpacity: 0.2,
  weight: 1,
};
const DEFAULT_MAP_ZOOM = 15;
const DEFAULT_LABEL_MIN_ZOOM = 12;

const LABEL_STYLES = `
  .geo-fence-label-layer {
    background: transparent !important;
    border: none !important;
  }

  .geo-fence-label-layer .geo-fence-label-text {
    color: #333 !important;
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
    padding: 0 !important;
    margin: 0 !important;
    border-radius: 0 !important;
    text-align: center !important;
    white-space: nowrap !important;
    pointer-events: none !important;
    z-index: 1000 !important;
    font-family: Arial, sans-serif !important;
    line-height: 1.2 !important;
    font-weight: 100 !important;
  }
`;

function resolveOrganisationId(asset, organisationId) {
  if (organisationId) return organisationId;
  const assetOrg = asset && asset.organisation;
  if (!assetOrg) return null;
  if (typeof assetOrg === 'string') return assetOrg;
  return assetOrg._id || assetOrg.id || null;
}

function getFeatureStyle(fence) {
  const featureStyle = fence && fence.feature && fence.feature.properties && fence.feature.properties.style;
  return {
    ...DEFAULT_STYLE,
    ...(featureStyle || {}),
    color: (featureStyle && featureStyle.color) || fence.color || DEFAULT_STYLE.color,
    fillColor: (featureStyle && featureStyle.fillColor) || fence.fillColor || fence.color || DEFAULT_STYLE.fillColor,
    fillOpacity: typeof ((featureStyle && featureStyle.fillOpacity) || fence.fillOpacity) === 'number'
      ? ((featureStyle && featureStyle.fillOpacity) || fence.fillOpacity)
      : DEFAULT_STYLE.fillOpacity,
    weight: typeof ((featureStyle && featureStyle.weight) || fence.weight) === 'number'
      ? ((featureStyle && featureStyle.weight) || fence.weight)
      : DEFAULT_STYLE.weight,
  };
}

function getFenceName(fence) {
  return (
    (fence && fence.name) ||
    (fence && fence.feature && fence.feature.properties && fence.feature.properties.name) ||
    'Point of Interest'
  );
}

function getFenceLabelText(rawName) {
  const normalized = String(rawName || '').trim();
  if (!normalized) return '';
  return normalized;
}

function escapeHtml(text) {
  return String(text || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function isLngLatPair(value) {
  return Array.isArray(value)
    && value.length >= 2
    && Number.isFinite(Number(value[0]))
    && Number.isFinite(Number(value[1]));
}

function flattenLngLatPairs(coordinates, output = []) {
  if (!Array.isArray(coordinates)) return output;
  if (isLngLatPair(coordinates)) {
    output.push([Number(coordinates[0]), Number(coordinates[1])]);
    return output;
  }
  coordinates.forEach((item) => flattenLngLatPairs(item, output));
  return output;
}

function getGeometryCenter(geometry) {
  if (!geometry || !geometry.type) return null;
  const { type, coordinates } = geometry;

  if (type === 'Point' && isLngLatPair(coordinates)) {
    return [Number(coordinates[1]), Number(coordinates[0])];
  }

  const pairs = flattenLngLatPairs(coordinates, []);
  if (!pairs.length) return null;

  let minLng = pairs[0][0];
  let maxLng = pairs[0][0];
  let minLat = pairs[0][1];
  let maxLat = pairs[0][1];

  pairs.forEach(([lng, lat]) => {
    minLng = Math.min(minLng, lng);
    maxLng = Math.max(maxLng, lng);
    minLat = Math.min(minLat, lat);
    maxLat = Math.max(maxLat, lat);
  });

  return [(minLat + maxLat) / 2, (minLng + maxLng) / 2];
}

function getFeatureCenter(feature) {
  try {
    if (!feature || feature.type !== 'Feature' || !feature.geometry) return null;
    const centeredFeature = pointOnFeature(feature);
    const coordinates = centeredFeature && centeredFeature.geometry && centeredFeature.geometry.coordinates;
    if (Array.isArray(coordinates) && coordinates.length >= 2) {
      return [Number(coordinates[1]), Number(coordinates[0])];
    }
  } catch (e) {
    // Fallback to bbox center below.
  }

  const geometry = feature && feature.geometry;
  return getGeometryCenter(geometry);
}

function getLabelPosition(fence) {
  return getFeatureCenter(fence && fence.feature);
}

function buildLabelIcon(label, mapZoom) {
  const compactLabel = getFenceLabelText(label);
  const safeLabel = escapeHtml(compactLabel);
  const zoomValue = Number.isFinite(Number(mapZoom)) ? Number(mapZoom) : DEFAULT_MAP_ZOOM;
  const fontSize = Math.max(5, Math.min(10, 10 + zoomValue * 0.6));
  return L.divIcon({
    className: 'geo-fence-label-layer',
    html: `<span class="geo-fence-label-text" style="font-size:${fontSize}px;">${safeLabel}</span>`,
    iconSize: [0, 0],
    iconAnchor: [0, 0],
  });
}

function buildFenceLabelFeatureCollection(geoFences) {
  const features = (geoFences || []).map((fence, index) => {
    const position = getLabelPosition(fence);
    const name = getFenceName(fence);
    if (!position || !name) return null;
    return {
      type: 'Feature',
      id: fence && fence._id ? fence._id : `fence-${index}`,
      geometry: {
        type: 'Point',
        coordinates: [Number(position[1]), Number(position[0])],
      },
      properties: {
        name,
      },
    };
  }).filter(Boolean);

  return {
    type: 'FeatureCollection',
    features,
  };
}

export default function GeoFenceMapOverlay({
  organisationId,
  asset,
  interactive = false,
  mapZoom = DEFAULT_MAP_ZOOM,
  labelMinZoom = DEFAULT_LABEL_MIN_ZOOM,
  hiddenCategories = [],
  onCategoriesLoaded,
}) {
  const [geoFences, setGeoFences] = useState([]);

  const resolvedOrganisationId = useMemo(
    () => resolveOrganisationId(asset, organisationId),
    [asset, organisationId]
  );
  useEffect(() => {
    if (!hasSession()) {
      setGeoFences([]);
      return;
    }

    let didCancel = false;
    const url = resolvedOrganisationId
      ? `/geo-fence/visible?organisation=${encodeURIComponent(resolvedOrganisationId)}`
      : '/geo-fence/visible';

    axios.get(url)
      .then((res) => {
        if (didCancel) return;
        const fences = Array.isArray(res.data) ? res.data : [];
        setGeoFences(fences);
        if (typeof onCategoriesLoaded === 'function') {
          const cats = Array.from(new Set(fences.map((f) => (f && f.category) || 'other')));
          onCategoriesLoaded(cats);
        }
      })
      .catch(() => {
        if (didCancel) return;
        setGeoFences([]);
      });

    return () => {
      didCancel = true;
    };
  }, [resolvedOrganisationId]);

  const visibleFences = useMemo(() => {
    if (!hiddenCategories || !hiddenCategories.length) return geoFences;
    return geoFences.filter((f) => {
      const cat = (f && f.category) || 'other';
      return !hiddenCategories.includes(cat);
    });
  }, [geoFences, hiddenCategories]);

  const labelLayerGeoJson = useMemo(
    () => buildFenceLabelFeatureCollection(visibleFences),
    [visibleFences]
  );

  if (!visibleFences.length) return null;

  return (
    <>
      <style>{LABEL_STYLES}</style>
      {visibleFences.map((fence, idx) => (
        <GeoJSON
          key={fence._id || `${getFenceName(fence)}-${idx}`}
          data={fence.feature}
          style={() => getFeatureStyle(fence)}
          interactive={interactive}
        />
      ))}
      {Number(mapZoom) >= Number(labelMinZoom) && labelLayerGeoJson && labelLayerGeoJson.features.length > 0 && (
        <GeoJSON
          key={`geo-fence-label-layer-${Number(mapZoom)}-${visibleFences.map((f) => f._id).join(',')}`}
          data={labelLayerGeoJson}
          interactive={false}
          pointToLayer={(feature, latlng) => {
            const label = feature && feature.properties && feature.properties.name;
            return L.marker(latlng, {
              icon: buildLabelIcon(label, mapZoom),
              interactive: false,
              keyboard: false,
            });
          }}
        />
      )}
    </>
  );
}
